import React from 'react';
import { PortfolioSlot, SlotsOrder } from 'src/redux/user_portfolio/user_portfolio.types';
import Icon from 'src/components/common/Icon';
import { EmptyState, SortHeaderButton, StatusBadge, Table, TableWrapper } from './SlotsTable.styles';

export const SLOTS_TABLE_EMPTY_COPY = 'No hay slots para mostrar.';

interface SlotsTableProps {
    slots: PortfolioSlot[];
    /** Muestra la columna de selección (checkbox). */
    selectable?: boolean;
    selectedIds?: number[];
    onToggle?: (id: number) => void;
    emptyCopy?: string;
    /** Orden activo por fecha de expiración; habilita el encabezado clickeable. */
    expirationSort?: SlotsOrder | null;
    /** Si se provee, "Fecha expiración" se vuelve clickeable para alternar el orden. */
    onToggleExpirationSort?: () => void;
}

const formatProperty = (propertyId: number | null): string =>
    propertyId == null ? 'Sin asignar' : String(propertyId);

const formatStatus = (status: number): string => (status === 1 ? 'Activo' : 'Inactivo');

/** Icono del encabezado de orden según la dirección activa (sin orden = icono neutro). */
const SORT_ICON_BY_ORDER: Record<SlotsOrder, string> = {
    asc: 'pcom-icon-chevron-up',
    desc: 'pcom-icon-chevron-down',
};
const NEUTRAL_SORT_ICON = 'pcom-icon-sort';

/**
 * Tabla reutilizable de slots (misma estructura de columnas para Vigencias y
 * Asignaciones): slot, propiedad asignada, fecha inicio, fecha expiración, meses
 * y estado. Con `selectable` agrega una columna de checkbox.
 */
const SlotsTable = ({
    slots,
    selectable = false,
    selectedIds = [],
    onToggle,
    emptyCopy = SLOTS_TABLE_EMPTY_COPY,
    expirationSort,
    onToggleExpirationSort,
}: SlotsTableProps) => {
    if (!slots.length) {
        return <EmptyState>{emptyCopy}</EmptyState>;
    }

    const sortIcon = expirationSort ? SORT_ICON_BY_ORDER[expirationSort] : NEUTRAL_SORT_ICON;

    return (
        <TableWrapper>
            <Table>
                <thead>
                    <tr>
                        {selectable && <th className="checkbox-cell" aria-label="Selección" />}
                        <th>Slot</th>
                        <th>Propiedad asignada</th>
                        <th>Fecha inicio</th>
                        <th>
                            {onToggleExpirationSort ? (
                                <SortHeaderButton
                                    type="button"
                                    className={expirationSort ? 'active' : ''}
                                    data-id="slots-sort-expiration"
                                    onClick={onToggleExpirationSort}
                                    aria-label="Ordenar por fecha de expiración"
                                >
                                    Fecha expiración
                                    <Icon
                                        className="sort-icon"
                                        icon={sortIcon}
                                        size={16}
                                        aria-hidden="true"
                                    />
                                </SortHeaderButton>
                            ) : (
                                'Fecha expiración'
                            )}
                        </th>
                        <th>Meses</th>
                        <th>Estado</th>
                    </tr>
                </thead>
                <tbody>
                    {slots.map((slot) => {
                        const isSelected = selectedIds.includes(slot.id);
                        return (
                            <tr key={slot.id} className={isSelected ? 'selected' : ''}>
                                {selectable && (
                                    <td className="checkbox-cell">
                                        <input
                                            type="checkbox"
                                            data-id={`slot-checkbox-${slot.id}`}
                                            checked={isSelected}
                                            onChange={() => onToggle?.(slot.id)}
                                        />
                                    </td>
                                )}
                                <td>{slot.id}</td>
                                <td>{formatProperty(slot.property_id)}</td>
                                <td>{slot.date_start || '-'}</td>
                                <td>{slot.date_end || '-'}</td>
                                <td>{slot.months}</td>
                                <td>
                                    <StatusBadge $active={slot.status === 1}>
                                        {formatStatus(slot.status)}
                                    </StatusBadge>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </TableWrapper>
    );
};

export default SlotsTable;
